'use client';

import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { Button } from '@pickleballinc/ui/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ScoreFormatConfig } from '@/components/constants/types';
import { MatchScoreWrapper } from '@/components/molecules/ScoreCards/MatchScoreWrapper';
import { AlertScoreModalContent } from '@/components/molecules/ScoreCards/modals/AlertScoreModalContent';
import { CancelMatchModalContent } from '@/components/molecules/ScoreCards/modals/CancelMatchModalContent';
import { ConfirmWithdrawMatchModal } from '@/components/molecules/ScoreCards/modals/ConfirmWithdrawMatchModal';
import { ForfeitAndRetireModalContent } from '@/components/molecules/ScoreCards/modals/ForfeitAndRetireModalContent';
import { PlayerNames } from '@/components/molecules/ScoreCards/ScoreCardWrapper';
import { ModalWrapper } from '@/components/organisms';
import { Match } from '@/entities';
import { getMatchupStatusApiValue, MatchCompletedEnum, MatchupStatusEnum } from '@/entities/matchup/constants';
import { GameScore } from '@/entities/score/GameScore';
import { RetireOrForfeitGameInfo } from '@/entities/score/RetireOrForfeitGameInfo';
import { matchKeys } from '@/hooks/matches/matchKeys';
import { useUpdateMatchMutation } from '@/hooks/matches/useUpdateMatch';
import { useUpdateMatchup } from '@/hooks/matches/useUpdateMatchup';
import { MatchScores } from '@/lib/types';
import { createMatchScoreUpdateData, isScoreIrregular } from '@/utils/helpers';
import { calculateScoreAfterRetireOrForfeit, determineInitialState, mapStateToMatchScores } from '@/utils/score-utils';

interface Props {
	matchupUuid: string;
	match: Match;
	awayTeamTitle: string;
	homeTeamTitle: string;
	awayTeamUuid: string;
	homeTeamUuid: string;
	matchupStatus: MatchupStatusEnum;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	playerNames: PlayerNames;
	canCancelMatch: boolean;
}

export interface MatchScoreState {
	games: { away: GameScore; home: GameScore }[];
	isModalOpen: boolean;
	retireOrForfeitGameInfo: RetireOrForfeitGameInfo | null;
	isCancelModalOpen: boolean;
}

const buttonLabels = {
	close: formatText({ text: 'Close', role: TextRole.ButtonLabel }),
	save: formatText({ text: 'Save', role: TextRole.ButtonLabel }),
	cancelMatch: formatText({ text: 'Cancel the match', role: TextRole.ButtonLabel }),
	uncancelMatch: formatText({ text: 'Uncancel the match', role: TextRole.ButtonLabel })
};

const toastMessages = {
	matchUpdated: formatText({ text: 'Match updated successfully', role: TextRole.AlertBody })
};

const getMatchScoreKey = (match: Match): string =>
	[
		match.teamOneGameOneScore,
		match.teamTwoGameOneScore,
		match.gameOneStatus,
		match.teamOneGameTwoScore,
		match.teamTwoGameTwoScore,
		match.gameTwoStatus,
		match.teamOneGameThreeScore,
		match.teamTwoGameThreeScore,
		match.gameThreeStatus,
		match.teamOneGameFourScore,
		match.teamTwoGameFourScore,
		match.gameFourStatus,
		match.teamOneGameFiveScore,
		match.teamTwoGameFiveScore,
		match.gameFiveStatus
	].join(',');

export const MatchScorecardModal = ({
	matchupUuid,
	match,
	awayTeamTitle,
	homeTeamTitle,
	matchupStatus,
	isOpen,
	onOpenChange,
	playerNames,
	canCancelMatch
}: Props) => {
	const [matchScorecardState, setMatchScorecardState] = useState(determineInitialState(match));
	const [isAlertScoreModalOpen, setIsAlertScoreModalOpen] = useState<boolean>(false);
	const [textWarningArray, setTextWarningArray] = useState<string[]>([]);
	const prevMatchRef = useRef({ matchUuid: match.matchUuid, scoreKey: getMatchScoreKey(match) });

	const queryClient = useQueryClient();
	const { mutate: updateMatchMutation, isPending: isUpdatingMatch } = useUpdateMatchMutation({
		onSuccessCallback: () => {
			toast.success(toastMessages.matchUpdated);
			queryClient.invalidateQueries({ queryKey: matchKeys.matches(matchupUuid) });
			onOpenChange(false);
		}
	});
	const { mutate: updateMatchupMutation } = useUpdateMatchup();

	const updateScore = (gameNumber: number, team: 'away' | 'home', newValue: GameScore) => {
		const fieldIndex = gameNumber - 1;
		setMatchScorecardState((oldState) => ({
			...oldState,
			games: oldState.games.map((game, i) =>
				i === fieldIndex
					? {
							away: team === 'away' ? newValue : game.away,
							home: team === 'home' ? newValue : game.home
						}
					: game
			)
		}));
	};

	useEffect(() => {
		const newScoreKey = getMatchScoreKey(match);
		const prev = prevMatchRef.current;
		// Re-initialize only when switching to a different match or when the current match's scores
		// were updated externally (e.g. via RTE). Ignores reference-only changes from unrelated RTE events.
		if (match.matchUuid !== prev.matchUuid || newScoreKey !== prev.scoreKey) {
			prevMatchRef.current = { matchUuid: match.matchUuid, scoreKey: newScoreKey };
			setMatchScorecardState(determineInitialState(match));
		}
	}, [match]);

	const scoreFormatConfig: ScoreFormatConfig = {
		maxGames: match.scoreFormatMaxGames ?? 0,
		gameBest: match.scoreFormatGameBest ?? 0,
		gameBestOutOf: match.scoreFormatGameBestOutOf ?? 0,
		games: [
			{ max: match.scoreFormatGame1Max ?? 0, winBy: match.scoreFormatGame1WinBy ?? 0 },
			{ max: match.scoreFormatGame2Max ?? 0, winBy: match.scoreFormatGame2WinBy ?? 0 },
			{ max: match.scoreFormatGame3Max ?? 0, winBy: match.scoreFormatGame3WinBy ?? 0 },
			{ max: match.scoreFormatGame4Max ?? 0, winBy: match.scoreFormatGame4WinBy ?? 0 },
			{ max: match.scoreFormatGame5Max ?? 0, winBy: match.scoreFormatGame5WinBy ?? 0 }
		]
	};

	const saveScore = () => {
		const currentScores: MatchScores = mapStateToMatchScores(matchScorecardState);
		const submittedScores = createMatchScoreUpdateData(currentScores, scoreFormatConfig);
		const textWarningArray = isScoreIrregular(scoreFormatConfig, submittedScores);
		setTextWarningArray(textWarningArray);

		if (textWarningArray.length > 0) {
			setIsAlertScoreModalOpen(true);
		} else {
			handleScoreSubmit();
		}
	};

	const handleScoreSubmit = () => {
		setIsAlertScoreModalOpen(false);
		const currentScores: MatchScores = mapStateToMatchScores(matchScorecardState);
		const submittedScores = createMatchScoreUpdateData(currentScores, scoreFormatConfig);
		updateMatchMutation(
			{
				matchId: match.matchUuid,
				updateData: submittedScores
			},
			{
				onSuccess: () => {
					if (matchupStatus !== MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS) {
						updateMatchupMutation({
							matchup_uuid: matchupUuid,
							matchup_status: getMatchupStatusApiValue(MatchupStatusEnum.IN_PROGRESS_MATCHUP_STATUS)
						});
					}
				}
			}
		);
	};

	const onRetireOrForfeitSelect = (side: 'away' | 'home', type: 'forfeit' | 'retire' | 'undo' | 'withdraw', gameNumber: number) => {
		setMatchScorecardState((oldState) => ({
			...oldState,
			retireOrForfeitGameInfo: {
				type: type,
				side: side,
				gameNumber: gameNumber,
				teamName: side === 'away' ? awayTeamTitle : homeTeamTitle
			}
		}));
	};

	const onRetireOrForfeitConfirm = () => {
		if (!matchScorecardState.retireOrForfeitGameInfo) return;

		const fieldIndex = matchScorecardState.retireOrForfeitGameInfo.gameNumber - 1;
		if (!matchScorecardState.games[fieldIndex]) {
			return;
		}
		const initialGame = determineInitialState(match).games[fieldIndex];
		const wasGameRetiredOrForfeitedOrWithdrawn =
			matchScorecardState.retireOrForfeitGameInfo.side === 'away'
				? initialGame?.away.isForfeited || initialGame?.away.isRetired || initialGame?.away.isWithdrawn
				: initialGame?.home.isForfeited || initialGame?.home.isRetired || initialGame?.home.isWithdrawn;
		const newScore = calculateScoreAfterRetireOrForfeit(
			matchScorecardState.games[fieldIndex].away.score,
			matchScorecardState.games[fieldIndex].home.score,
			matchScorecardState.retireOrForfeitGameInfo,
			match
		);

		setMatchScorecardState((oldState) => ({
			...oldState,
			games: oldState.games.map((game, i) =>
				i === fieldIndex
					? {
							away:
								matchScorecardState.retireOrForfeitGameInfo?.side === 'away'
									? {
											isRetired: matchScorecardState.retireOrForfeitGameInfo.type === 'retire',
											isForfeited: matchScorecardState.retireOrForfeitGameInfo.type === 'forfeit',
											isWithdrawn: matchScorecardState.retireOrForfeitGameInfo.type === 'withdraw',
											isUndo: Boolean(
												matchScorecardState.retireOrForfeitGameInfo.type === 'undo' && wasGameRetiredOrForfeitedOrWithdrawn
											),
											score: newScore.away
										}
									: {
											isRetired: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.away.isRetired,
											isForfeited: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.away.isForfeited,
											isWithdrawn: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.away.isWithdrawn,
											isUndo: false,
											score: newScore.away
										},
							home:
								matchScorecardState.retireOrForfeitGameInfo?.side === 'home'
									? {
											isRetired: matchScorecardState.retireOrForfeitGameInfo.type === 'retire',
											isForfeited: matchScorecardState.retireOrForfeitGameInfo.type === 'forfeit',
											isWithdrawn: matchScorecardState.retireOrForfeitGameInfo.type === 'withdraw',
											isUndo: Boolean(
												matchScorecardState.retireOrForfeitGameInfo.type === 'undo' && wasGameRetiredOrForfeitedOrWithdrawn
											),
											score: newScore.home
										}
									: {
											isRetired: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.home.isRetired,
											isForfeited: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.home.isForfeited,
											isWithdrawn: matchScorecardState.retireOrForfeitGameInfo?.type !== 'undo' ? false : game.home.isWithdrawn,
											isUndo: false,
											score: newScore.home
										}
						}
					: game
			),
			retireOrForfeitGameInfo: null
		}));
	};

	if (!isOpen) return null;

	const getOriginalActionType = (): 'retire' | 'forfeit' | 'withdraw' | undefined => {
		if (!matchScorecardState.retireOrForfeitGameInfo) return undefined;

		const gameIndex = matchScorecardState.retireOrForfeitGameInfo.gameNumber - 1;
		const side = matchScorecardState.retireOrForfeitGameInfo.side;
		const gameState = matchScorecardState.games[gameIndex]?.[side];

		if (gameState?.isRetired) return 'retire';
		if (gameState?.isForfeited) return 'forfeit';
		if (gameState?.isWithdrawn) return 'withdraw';

		return undefined;
	};

	const onClose = () => {
		onOpenChange(false);
		setMatchScorecardState(determineInitialState(match));
	};

	return (
		<ModalWrapper
			title={match.roundText || `${match.playerGroupTitle} ${match.formatTitle}`}
			onClose={onClose}
			showModalHeader={false}
			className="max-w-[430px] !max-h-[655px]"
		>
			{!matchScorecardState.retireOrForfeitGameInfo && !isAlertScoreModalOpen && !matchScorecardState.isCancelModalOpen && (
				<div className="flex flex-col gap-3">
					{/* Modal Header*/}
					<div className="relative w-full">
						<p className="text-center font-medium text-gray-900 text-lg">
							{match.roundText || `${match.playerGroupTitle} ${match.formatTitle}`}
						</p>
						<FontAwesomeIcon
							icon={faClose}
							className="cursor-pointer text-xl font-normal absolute right-0 top-1/2 -translate-y-1/2"
							onClick={onClose}
						/>
					</div>
					<hr />
					<p className="font-medium text-base text-gray-900 text-center">{match.scoreFormatShorthand}</p>
					<div className="flex flex-col items-center">
						<p className="text-gray-900 font-bold text-lg">{`${awayTeamTitle} (A)`}</p>
						<p className="text-gray-900 font-medium text-lg">{playerNames.teamTwoPlayerOneName}</p>
						{match.teamOnePlayerTwoName ? <p className="text-gray-900 font-medium text-lg">{playerNames.teamTwoPlayerTwoName}</p> : null}
					</div>

					<MatchScoreWrapper
						state={matchScorecardState}
						updateScore={updateScore}
						onRetireOrForfeitSelect={onRetireOrForfeitSelect}
						matchupStatus={matchupStatus}
						awayTeamFinalScore={match.teamTwoFinalScore}
						homeTeamFinalScore={match.teamOneFinalScore}
						winner={match.winner}
						isLocked={false}
						gameStatuses={[
							match.gameOneStatus!,
							match.gameTwoStatus!,
							match.gameThreeStatus!,
							match.gameFourStatus!,
							match.gameFiveStatus!
						]}
						scoreFormatMaxGames={match.scoreFormatMaxGames}
						isCancelled={match.matchCompletedType === MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE}
					/>

					<div className="flex flex-col items-center">
						<p className="text-gray-900 font-bold text-lg">{`${homeTeamTitle} (H)`}</p>
						<p className="text-gray-900 font-medium text-lg">{playerNames.teamOnePlayerOneName}</p>
						{match.teamTwoPlayerTwoName ? <p className="text-gray-900 font-medium text-lg">{playerNames.teamOnePlayerTwoName}</p> : null}
					</div>

					<div className="flex gap-4 w-full justify-end">
						<Button className={`h-10 w-1/2 text-sm font-semibold leading-5`} onClick={onClose} variant="outline">
							{buttonLabels.close}
						</Button>
						<Button
							onClick={saveScore}
							disabled={isUpdatingMatch}
							className="h-10 w-1/2 text-sm font-semibold leading-5 disabled:cursor-not-allowed"
						>
							{buttonLabels.save}
						</Button>
					</div>
					{!!canCancelMatch && (
						<Button
							className="text-center text-orange-500 hover:text-orange-700 text-sm font-semibold hover:bg-transparent"
							onClick={() => {
								setMatchScorecardState((prevState) => ({ ...prevState, isCancelModalOpen: true }));
							}}
							variant="ghost"
						>
							{match.matchCompletedType === MatchCompletedEnum.CANCELED_MATCH_COMPLETED_TYPE
								? buttonLabels.uncancelMatch
								: buttonLabels.cancelMatch}
						</Button>
					)}
				</div>
			)}
			{matchScorecardState.retireOrForfeitGameInfo ? (
				matchScorecardState.retireOrForfeitGameInfo.type === 'withdraw' ? (
					<ConfirmWithdrawMatchModal
						setShowModal={(show: boolean) => {
							if (!show) {
								setMatchScorecardState((prevState) => ({ ...prevState, retireOrForfeitGameInfo: null }));
							}
						}}
						teamName={matchScorecardState.retireOrForfeitGameInfo.teamName}
						gameNumber={matchScorecardState.retireOrForfeitGameInfo.gameNumber}
						onConfirm={onRetireOrForfeitConfirm}
					/>
				) : (
					<ForfeitAndRetireModalContent
						type={matchScorecardState.retireOrForfeitGameInfo.type}
						isUndo={matchScorecardState.retireOrForfeitGameInfo.type === 'undo'}
						side={matchScorecardState.retireOrForfeitGameInfo.side}
						gameNumber={matchScorecardState.retireOrForfeitGameInfo.gameNumber}
						teamName={matchScorecardState.retireOrForfeitGameInfo.teamName}
						originalActionType={getOriginalActionType()}
						onCancel={() => {
							setMatchScorecardState((prevState) => ({ ...prevState, retireOrForfeitGameInfo: null }));
						}}
						onConfirm={onRetireOrForfeitConfirm}
					/>
				)
			) : null}
			{isAlertScoreModalOpen ? (
				<AlertScoreModalContent
					handleSubmit={handleScoreSubmit}
					handleCancel={() => setIsAlertScoreModalOpen(false)}
					textWarningArray={textWarningArray}
				/>
			) : null}
			{matchScorecardState.isCancelModalOpen ? (
				<CancelMatchModalContent
					matchId={match.matchUuid}
					matchupUuid={matchupUuid}
					onCancel={() => {
						setMatchScorecardState((prevState) => ({ ...prevState, isCancelModalOpen: false }));
					}}
					onCancelMatchSuccess={() => {
						setMatchScorecardState((prevState) => ({ ...prevState, isCancelModalOpen: false }));
						onOpenChange(false);
					}}
					matchStatus={match.matchCompletedType}
				/>
			) : null}
		</ModalWrapper>
	);
};
